import { useState, useCallback, useRef } from 'react';
import { searchAddress, retrieveAddress, resetSessionToken } from '../services/mapboxService';

/**
 * Hook para búsqueda de direcciones con Mapbox
 */
export const useAddressSearch = (options = {}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const debounceRef = useRef(null);

  // Buscar direcciones con debounce
  const search = useCallback(async (searchQuery) => {
    setQuery(searchQuery);

    // Limpiar debounce anterior
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // No buscar si es muy corto
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    // Debounce de 300ms
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchAddress(searchQuery, options);
        setSuggestions(results);
      } catch (error) {
        console.error('Error en búsqueda:', error);
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [options]);

  // Seleccionar una dirección de las sugerencias
  const selectAddress = useCallback(async (suggestion) => {
    setIsSearching(true);
    try {
      const fullAddress = await retrieveAddress(suggestion.id);
      if (fullAddress) {
        setSelectedAddress(fullAddress);
        setQuery(fullAddress.fullAddress);
        setSuggestions([]);
        resetSessionToken(); // Resetear para la próxima búsqueda
        return fullAddress;
      }
    } catch (error) {
      console.error('Error seleccionando dirección:', error);
    } finally {
      setIsSearching(false);
    }
    return null;
  }, []);

  // Limpiar búsqueda
  const clear = useCallback(() => {
    setQuery('');
    setSuggestions([]);
    setSelectedAddress(null);
    resetSessionToken();
  }, []);

  return {
    query,
    setQuery: search,
    suggestions,
    isSearching,
    selectedAddress,
    selectAddress,
    clear,
  };
};
